import { useLabelDialogConfig } from '@aibees/elephant/hooks/labels/use-label-dialog-config';
import { LabelType } from '@aibees/elephant/types/label';
import {
  DialogActions,
  DialogTitle,
  Form,
  FormOnSubmit,
} from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { Dialog, DialogContent, Fade } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { ActionBulkModalAiLookalike } from '../action-bulk-modal-ai-lookalike';
import { AssignOwner } from '../assign-owner';
import { CreateActionBulkDialog } from '../create-action-bulk-dialog';
import { ListLabelActionBulkModal } from '../list-label-action-bulk-modal';
import { scheme } from './config';
import type { SaveDialogProps as Props } from './types';
import { useSaveDefaultValues } from './use-save-default-values';
import { useSaveDialog } from './use-save-dialog';

// Memoized child components (CreateActionBulkDialog wrapped in component to avoid circular dependency TDZ)
const MemoizedListLabelModal = memo(ListLabelActionBulkModal);
const MemoizedAssignOwner = memo(AssignOwner);
const MemoizedAiLookalike = memo(ActionBulkModalAiLookalike);
const MemoizedDialogTitle = memo(DialogTitle);
const MemoizedDialogFooter = memo(DialogActions);

export const SaveDialog = memo(({ className, element }: Props) => {
  // Custom hook for save dialog logic
  const {
    saveDialogState,
    ids,
    expandedAccordion,
    showAiLookalike,
    workspace,
    grouping,
    handleSave,
    handleClose,
    setExpandedAccordion,
    setPreviousState,
  } = useSaveDialog();

  // Deferred to avoid circular dependency "before initialization" (CreateActionBulkDialog)
  const MemoizedCreateDialog = useMemo(() => memo(CreateActionBulkDialog), []);

  // Local state
  const loading = useBoolean(false);
  const createListDialogState = useBoolean(false);
  const createLabelDialogState = useBoolean(false);
  const defaultValues = useSaveDefaultValues();

  // Handlers
  const handleSubmit = useCallback<FormOnSubmit>(
    (data) => {
      handleSave(data, {
        startProgress: loading.onTrue,
        endProgress: loading.onFalse,
        toggle: () => handleClose('save'),
      });
    },
    [handleSave, handleClose, loading.onTrue, loading.onFalse]
  );
  const {
    inputTitle: listInputTitle,
    titleDialog: listTitleDialog,
    placeholder: listPlaceholder,
  } = useLabelDialogConfig({ isLabel: false });
  const {
    inputTitle: labelInputTitle,
    titleDialog: labelTitleDialog,
    placeholder: labelPlaceholder,
  } = useLabelDialogConfig({ isLabel: true });

  const handleCloseDialog = useCallback(() => {
    handleClose('save');
    setPreviousState('');
  }, [handleClose, setPreviousState]);

  return (
    <>
      {element}
      <Form
        id="save-dialog"
        scheme={scheme}
        defaultValues={defaultValues}
        loading={loading.value}
        customSubmitButton
        sx={{ display: 'none' }}
        onSubmit={handleSubmit}
      >
        <Dialog
          open={saveDialogState.value}
          fullWidth={true}
          maxWidth="xs"
          TransitionComponent={Fade}
          onClose={() => handleClose('save')}
        >
          <MemoizedDialogTitle
            title={`Save Records (${ids.length})`}
            icon="save"
            onClose={handleCloseDialog}
          />

          <DialogContent padding="none">
            <MemoizedListLabelModal
              key={`list-${ids.length}`}
              type={LabelType.LIST}
              state={createListDialogState}
              firstState={saveDialogState}
              optional={true}
              expandedAccordion={expandedAccordion}
              setExpandedAccordion={setExpandedAccordion}
            />
            <MemoizedListLabelModal
              key={`label-${ids.length}`}
              type={LabelType.LABEL}
              state={createLabelDialogState}
              firstState={saveDialogState}
              optional={true}
              expandedAccordion={expandedAccordion}
              setExpandedAccordion={setExpandedAccordion}
            />
            {workspace && (
              <MemoizedAssignOwner
                expandedAccordion={expandedAccordion}
                setExpandedAccordion={setExpandedAccordion}
              />
            )}
            <MemoizedAiLookalike
              expandedAccordion={expandedAccordion}
              setExpandedAccordion={setExpandedAccordion}
            />
          </DialogContent>
          <MemoizedDialogFooter
            formId="save-dialog"
            onClose={handleCloseDialog}
          />
        </Dialog>

        <MemoizedCreateDialog
          state={createListDialogState}
          firstState={saveDialogState}
          title={listTitleDialog}
          type={LabelType.LIST}
          inputTitle={listInputTitle}
          placeholder={listPlaceholder}
          grouping={grouping}
        />

        <MemoizedCreateDialog
          state={createLabelDialogState}
          firstState={saveDialogState}
          title={labelTitleDialog}
          type={LabelType.LABEL}
          inputTitle={labelInputTitle}
          hasColor={true}
          hasDescription={true}
          placeholder={labelPlaceholder}
          grouping={grouping}
        />
      </Form>
    </>
  );
});

SaveDialog.displayName = 'SaveDialog';
